import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Animated } from 'react-native';
import FontFamily from '../../../constants/fonts';
import { useTheme } from '../../../theme';

export interface AdminCardTag {
  label: string;
  bgColor: string;
  textColor: string;
}

export interface AdminCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  tags?: AdminCardTag[];
  onPress?: () => void;
  /** When true, renders animated skeleton placeholders instead of tags */
  kfaSkeletonLoading?: boolean;
  /** Animated value driving the skeleton pulse (passed from parent) */
  kfaPulseAnim?: Animated.Value;
}

const AdminCard: React.FC<AdminCardProps> = ({ icon, title, description, tags, onPress, kfaSkeletonLoading, kfaPulseAnim }) => {
  const { resolvedTheme } = useTheme();
  // Apply dark-mode overrides on all platforms; web also gets explicit overrides
  // so that inline TAG_DARK badge colors (set as JS values, bypassing the CSS patcher)
  // render against a guaranteed dark card background rather than the default white.
  const isDark = resolvedTheme === 'dark';

  return (
    <TouchableOpacity
      style={[styles.card, isDark && { backgroundColor: '#202020' }]}
      activeOpacity={0.85}
      onPress={onPress}
    >
      <View style={[styles.iconCircle, isDark && { backgroundColor: 'rgba(61,139,255,0.18)' }]}>{icon}</View>
      <Text style={[styles.cardTitle, { color: isDark ? '#F2F2F2' : '#2D2D2D' }]}>{title}</Text>
      <Text style={[styles.cardDescription, { color: isDark ? '#B0B0B0' : '#616161' }]}>{description}</Text>
      {kfaSkeletonLoading && kfaPulseAnim ? (
        <View style={styles.cardFooter}>
          {[80, 72, 76].map((w, i) => (
            <Animated.View
              key={i}
              style={[styles.tag, styles.skeletonTag, { width: w, opacity: kfaPulseAnim }]}
            />
          ))}
        </View>
      ) : tags && tags.length > 0 ? (
        <View style={styles.cardFooter}>
          {tags.map((tag, index) => (
            <View key={index} style={[styles.tag, { backgroundColor: tag.bgColor }]}>
              <Text style={[styles.tagText, { color: tag.textColor }]}>{tag.label}</Text>
            </View>
          ))}
        </View>
      ) : null}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    padding: 24,
    width: 280,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  iconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#E8F0FB',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#2D2D2D',
    marginBottom: 6,
    fontFamily: FontFamily.bold,
  },
  cardDescription: {
    fontSize: 12,
    color: '#616161',
    lineHeight: 20,
    marginBottom: 16,
    fontFamily: FontFamily.regular,
  },
  cardFooter: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  tag: {
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  tagText: {
    fontSize: 11,
    fontWeight: '500',
    fontFamily: FontFamily.medium,
  },
  skeletonTag: {
    borderRadius: 4,
    backgroundColor: '#D0D9E8',
    alignSelf: 'flex-start',
  },
});

export default AdminCard;
